import React, { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../supabaseClient'
import { uploadDocumentoCliente, validarArquivoDocumento } from '../services/documentosClienteApi'
import { sincronizarEventoGoogleCalendar } from '../services/googleCalendarApi'
import './OrdemServico.css'

const EMPRESA_ID_ADMINISTRATIVO_PODE_ESCOLHER_OS = 'f0c6de5a-eaf9-4530-a6a7-28140091897d'

/** Tipos de anexo opcionais na OS (mesmo método do cadastro do cliente). */
const TIPOS_ANEXO_OS = [
  { value: 'unifilar', label: 'Unifilar' },
  { value: 'analise_sombreamento', label: 'Análise de Sombreamento' },
  { value: 'vistoria', label: 'Vistoria' }
]

const OrdemServico = ({ onBack, userRole, userEmpresa, userId, initialClienteParaOS, onConsumedInitialCliente }) => {
  // Função para formatar data sem hora (evita problemas de timezone)
  const formatarDataSemHora = (dataString) => {
    if (!dataString) return 'N/A'
    
    try {
      // Se já estiver no formato correto (string), formatar diretamente
      if (typeof dataString === 'string') {
        // Formato esperado: "YYYY-MM-DD" ou "YYYY-MM-DDTHH:mm:ss.sssZ"
        // Remover qualquer parte de hora/timezone
        const dataLimpa = dataString.split('T')[0].trim()
        const partes = dataLimpa.split('-')
        
        if (partes.length === 3) {
          const [ano, mes, dia] = partes
          // Validar se são números válidos
          if (ano && mes && dia && !isNaN(ano) && !isNaN(mes) && !isNaN(dia)) {
            return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`
          }
        }
      }
      
      // Se for um objeto Date, extrair apenas a data local (sem timezone)
      if (dataString instanceof Date) {
        const ano = dataString.getFullYear()
        const mes = String(dataString.getMonth() + 1).padStart(2, '0')
        const dia = String(dataString.getDate()).padStart(2, '0')
        return `${dia}/${mes}/${ano}`
      }
      
      // Tentar converter para string e processar
      const dataStr = String(dataString)
      if (dataStr.includes('-')) {
        const partes = dataStr.split('T')[0].split('-')
        if (partes.length === 3) {
          const [ano, mes, dia] = partes
          if (ano && mes && dia) {
            return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`
          }
        }
      }
    } catch (error) {
      console.warn('Erro ao formatar data:', error, dataString)
    }
    
    return 'N/A'
  }
  
  // Estados para o modal
  const [showModal, setShowModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editingOrdem, setEditingOrdem] = useState(null)
  
  // Estados para modais adicionais
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [ordemToDelete, setOrdemToDelete] = useState(null)
  const [showExcluirModal, setShowExcluirModal] = useState(false)
  const [ordemToExcluir, setOrdemToExcluir] = useState(null)
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [ordemInfo, setOrdemInfo] = useState(null)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [showConflitoModal, setShowConflitoModal] = useState(false)
  const [showConflitoInstalacaoModal, setShowConflitoInstalacaoModal] = useState(false)
  
  // Estados para dados auxiliares
  const [clientes, setClientes] = useState([])
  const [tiposServico, setTiposServico] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [equipes, setEquipes] = useState([])
  const [empresas, setEmpresas] = useState([])
  const [empresaId, setEmpresaId] = useState('')
  
  // Estados para searchbox de clientes
  const [clienteSearch, setClienteSearch] = useState('')
  const [showClienteDropdown, setShowClienteDropdown] = useState(false)
  const [selectedCliente, setSelectedCliente] = useState(null)
  
  // Estados para listagem de ordens
  const [ordensServico, setOrdensServico] = useState([])
  const [isLoadingOrdens, setIsLoadingOrdens] = useState(true)
  const [filtros, setFiltros] = useState({
    cliente: '',
    tipoServico: '',
    status: ''
  })
  
  // Estado para o formulário
  const [formData, setFormData] = useState({
    id_cliente: '',
    id_tipo_servico: '',
    id_equipe: '',
    status: 'Novo',
    obs_operacional: '',
    data_hora_execucao: (() => {
      const d = new Date()
      const date = d.toISOString().split('T')[0]
      const time = '08:00'
      return `${date}T${time}`
    })(),
    presenca: false,
    responsaveis: []
  })

  // Anexos opcionais (Unifilar / Análise de Sombreamento / Vistoria) — visível para todos com acesso à tela
  const [anexos, setAnexos] = useState([])
  const [anexoTipo, setAnexoTipo] = useState(TIPOS_ANEXO_OS[0].value)
  const [anexoErro, setAnexoErro] = useState('')

  const modalAbertoRef = useRef(false)
  const empresaNoModalAoAbrirRef = useRef('')

  const isAdmin = userRole === 'administrador'
  const podeSelecionarEmpresaNaOS =
    isAdmin ||
    (userRole === 'administrativo' && userEmpresa?.id === EMPRESA_ID_ADMINISTRATIVO_PODE_ESCOLHER_OS)
  /** Administrativo desta empresa: mesma visão de clientes que em Cadastrar Clientes (todas as empresas). */
  const verTodosClientesNaOS =
    userRole === 'administrativo' && userEmpresa?.id === EMPRESA_ID_ADMINISTRATIVO_PODE_ESCOLHER_OS
  const empresaUsuarioId = userEmpresa?.id || userEmpresa || ''
  const empresaSelecionadaId = podeSelecionarEmpresaNaOS ? (empresaId || empresaUsuarioId) : empresaUsuarioId

  const empresasDropdownList = useMemo(() => {
    let list =
      empresas.length > 0
        ? empresas
        : userEmpresa
          ? [{ id: empresaUsuarioId, empresa: userEmpresa?.empresa || 'Empresa do usuário' }]
          : []
    if (
      isEditing &&
      editingOrdem?.id_empresa &&
      !list.some((e) => String(e.id) === String(editingOrdem.id_empresa))
    ) {
      list = [
        ...list,
        {
          id: editingOrdem.id_empresa,
          empresa: editingOrdem.empresa?.empresa || 'Empresa',
        },
      ]
    }
    return list
  }, [empresas, userEmpresa, isEditing, editingOrdem, empresaUsuarioId])

  // Carregar dados iniciais
  useEffect(() => {
    loadClientes()
    loadTiposServico()
    loadUsuarios()
    loadEquipes()
  }, [empresaSelecionadaId, userRole, userEmpresa?.id, userEmpresa, verTodosClientesNaOS])

  // Lista completa de empresas assim que o usuário pode escolher empresa (evita dropdown só com “a do usuário” até abrir o modal)
  useEffect(() => {
    if (!podeSelecionarEmpresaNaOS) return
    let cancel = false
    supabase
      .from('empresa')
      .select('id, empresa')
      .order('empresa')
      .then(({ data, error }) => {
        if (cancel || error) return
        setEmpresas(data || [])
      })
    return () => {
      cancel = true
    }
  }, [podeSelecionarEmpresaNaOS])

  useEffect(() => {
    if (!showModal) return

    if (podeSelecionarEmpresaNaOS) {
      supabase
        .from('empresa')
        .select('id, empresa')
        .order('empresa')
        .then(({ data, error }) => {
          if (!error) setEmpresas(data || [])
        })
      // Ao abrir “Nova ordem”, volta a empresa padrão; em “Editar”, handleEditOrdem já definiu empresaId da ordem — não sobrescrever
      if (empresaUsuarioId && !isEditing) {
        setEmpresaId(empresaUsuarioId)
      }
    } else {
      setEmpresas([])
      if (empresaUsuarioId && !isEditing) {
        setEmpresaId(empresaUsuarioId)
      }
    }
  }, [showModal, podeSelecionarEmpresaNaOS, empresaUsuarioId, isEditing])

  // Recarregar ordens quando userRole/userEmpresa estiverem disponíveis
  useEffect(() => {
    const podeCarregar = userRole === 'administrador' || (userEmpresa && userEmpresa.id)
    if (!podeCarregar) return
    loadOrdensServico()
  }, [userRole, userEmpresa])

  // Abrir modal Nova Ordem com cliente pré-selecionado (vindo da Gestão de Clientes)
  useEffect(() => {
    if (!initialClienteParaOS || !initialClienteParaOS.id) return
    const cliente = {
      id: initialClienteParaOS.id,
      nome_completo: initialClienteParaOS.nome_completo || ''
    }
    setFormData(prev => ({
      ...prev,
      id_cliente: cliente.id,
      id_tipo_servico: '',
      id_equipe: '',
      status: 'Novo',
      obs_operacional: '',
      data_hora_execucao: (() => {
        const d = new Date()
        return `${d.toISOString().split('T')[0]}T08:00`
      })(),
      presenca: false,
      responsaveis: []
    }))
    setClienteSearch(cliente.nome_completo)
    setSelectedCliente(cliente)
    setShowClienteDropdown(false)
    setShowModal(true)
    setIsEditing(false)
    setEditingOrdem(null)
    if (typeof onConsumedInitialCliente === 'function') onConsumedInitialCliente()
  }, [initialClienteParaOS])

  // Função para carregar ordens de serviço
  const loadOrdensServico = async () => {
    try {
      setIsLoadingOrdens(true)
      
      let ordensData = []
      
      // Administrador ou administrativo (empresa f0c6…): ver todas as ordens (todas as empresas)
      if (podeSelecionarEmpresaNaOS) {
        const { data, error } = await supabase
          .from('ordens_servicos')
          .select(`
            id,
            id_cliente,
            id_tipo_servico,
            id_equipe,
            id_empresa,
            status,
            obs_operacional,
            obs_campo,
            data_execucao,
            data_criacao,
            created_at,
            updated_at,
            presenca,
            equipe:equipes(id, nome)
          `)
          .order('created_at', { ascending: false })
        
        if (error) throw error
        ordensData = data || []
      } else {
        // Se não for administrador, buscar ordens da empresa do usuário
        // e também ordens que a empresa tem permissão de ver
        if (userEmpresa?.id) {
          // 1. Buscar ordens da empresa do usuário
          const { data: ordensEmpresa, error: errorEmpresa } = await supabase
            .from('ordens_servicos')
            .select(`
              id,
              id_cliente,
              id_tipo_servico,
              id_equipe,
              id_empresa,
              status,
              obs_operacional,
              obs_campo,
              data_execucao,
              data_criacao,
              created_at,
              updated_at,
              presenca,
              equipe:equipes(id, nome)
            `)
            .eq('id_empresa', userEmpresa.id)
            .order('created_at', { ascending: false })
          
          if (errorEmpresa) throw errorEmpresa
          const ordensEmpresaList = ordensEmpresa || []
          console.log('📋 Ordens da empresa do usuário (OrdemServico):', ordensEmpresaList.length)
          
          // 2. Buscar permissões de instalações autorizadas
          const { data: permissoes, error: errorPermissoes } = await supabase
            .from('permissoes_instalacoes')
            .select('instalacao_id')
            .eq('empresa_autorizada_id', userEmpresa.id)
          
          if (errorPermissoes) {
            console.error('❌ Erro ao buscar permissões:', errorPermissoes)
            // Não lançar erro, apenas continuar sem permissões adicionais
          }
          
          const instalacaoIdsPermitidos = (permissoes || []).map(p => p.instalacao_id)
          console.log('🔐 Instalações autorizadas encontradas (OrdemServico):', instalacaoIdsPermitidos.length)
          
          // 3. Buscar ordens das instalações autorizadas (que não sejam da própria empresa)
          if (instalacaoIdsPermitidos.length > 0) {
            const { data: ordensPermitidas, error: errorPermitidas } = await supabase
              .from('ordens_servicos')
              .select(`
                id,
                id_cliente,
                id_tipo_servico,
                id_equipe,
                id_empresa,
                status,
                obs_operacional,
                obs_campo,
                data_execucao,
                data_criacao,
                created_at,
                updated_at,
                presenca,
                equipe:equipes(id, nome)
              `)
              .in('id_cliente', instalacaoIdsPermitidos)
              .neq('id_empresa', userEmpresa.id) // Excluir ordens da própria empresa (já estão em ordensEmpresaList)
              .order('created_at', { ascending: false })
            
            if (errorPermitidas) {
              console.error('❌ Erro ao buscar ordens permitidas:', errorPermitidas)
              // Não lançar erro, apenas continuar sem ordens adicionais
            } else {
              const ordensPermitidasList = ordensPermitidas || []
              console.log('📋 Ordens permitidas encontradas (OrdemServico):', ordensPermitidasList.length)
              
              // 4. Combinar ordens (usar Map para evitar duplicatas)
              const ordensMap = new Map()
              ordensEmpresaList.forEach(ordem => {
                ordensMap.set(ordem.id, ordem)
              })
              ordensPermitidasList.forEach(ordem => {
                ordensMap.set(ordem.id, ordem)
              })
              
              // Converter para array e ordenar por data
              ordensData = Array.from(ordensMap.values()).sort((a, b) => {
                const dateA = new Date(a.created_at || a.data_criacao || 0)
                const dateB = new Date(b.created_at || b.data_criacao || 0)
                return dateB - dateA // Ordem decrescente (mais recente primeiro)
              })
            }
          } else {
            ordensData = ordensEmpresaList
          }
          
          console.log('📋 Total de ordens encontradas (OrdemServico, com permissões):', ordensData.length)
        }
      }
      
      // Log para debug - verificar se data_execucao está vindo corretamente
      console.log('📋 Ordens carregadas (dados brutos):', ordensData?.map(o => ({ 
        id: o.id, 
        data_execucao: o.data_execucao,
        tipo_data_execucao: typeof o.data_execucao,
        data_execucao_formatada: formatarDataSemHora(o.data_execucao),
        updated_at: o.updated_at 
      })))
      
      // Buscar dados relacionados em lote para melhor performance
      if (ordensData && ordensData.length > 0) {
        // Extrair IDs únicos
        const clienteIds = [...new Set(ordensData.map(o => o.id_cliente).filter(Boolean))]
        const tipoServicoIds = [...new Set(ordensData.map(o => o.id_tipo_servico).filter(Boolean))]
        const empresaIds = [...new Set(ordensData.map(o => o.id_empresa).filter(Boolean))]
        const equipeIds = [...new Set(ordensData.map(o => o.id_equipe).filter(Boolean))]
        
        // Buscar todos os clientes de uma vez
        const { data: clientesData } = await supabase
          .from('clientes_cadastro')
          .select('id, nome_completo')
          .in('id', clienteIds)
        
        // Buscar todos os tipos de serviço de uma vez
        const { data: tiposServicoData } = await supabase
          .from('tipo_servico')
          .select('id, nome')
          .in('id', tipoServicoIds)
        
        // Buscar todas as empresas de uma vez
        const { data: empresasData } = await supabase
          .from('empresa')
          .select('id, empresa')
          .in('id', empresaIds)
        
        // Buscar todas as equipes de uma vez (se houver IDs)
        let equipesMap = new Map()
        if (equipeIds.length > 0) {
          const { data: equipesData } = await supabase
            .from('equipes')
            .select('id, nome')
            .in('id', equipeIds)
          
          equipesMap = new Map((equipesData || []).map(e => [e.id, e]))
        }
        
        // Criar mapas para lookup rápido
        const clientesMap = new Map((clientesData || []).map(c => [c.id, c.nome_completo]))
        const tiposServicoMap = new Map((tiposServicoData || []).map(t => [t.id, t.nome]))
        const empresasMap = new Map((empresasData || []).map(e => [e.id, e.empresa]))
        
        // Combinar dados
        const ordensCompletas = ordensData.map(ordem => ({
          ...ordem,
          cliente: { nome_completo: clientesMap.get(ordem.id_cliente) || 'N/A' },
          tipo_servico: { nome: tiposServicoMap.get(ordem.id_tipo_servico) || 'N/A' },
          empresa: { empresa: empresasMap.get(ordem.id_empresa) || 'N/A' },
          equipe: ordem.equipe || (ordem.id_equipe ? equipesMap.get(ordem.id_equipe) || { nome: 'N/A' } : null)
        }))
        
        console.log('📋 Ordens completas (após buscar relacionamentos):', ordensCompletas?.map(o => ({ 
          id: o.id, 
          data_execucao: o.data_execucao,
          updated_at: o.updated_at 
        })))
        
        setOrdensServico(ordensCompletas)
      } else {
        setOrdensServico([])
      }
    } catch (error) {
      console.error('Erro ao carregar ordens de serviço:', error)
    } finally {
      setIsLoadingOrdens(false)
    }
  }

  // Função para carregar clientes
  const loadClientes = async () => {
    try {
      let clientesData = []

      if (verTodosClientesNaOS) {
        const { data, error } = await supabase
          .from('clientes_cadastro')
          .select('id, nome_completo')
          .order('nome_completo')
        if (error) throw error
        clientesData = data || []
      }
      // Para quem pode selecionar empresa no modal, usar a empresa escolhida (exceto verTodosClientesNaOS acima)
      else if (podeSelecionarEmpresaNaOS && empresaSelecionadaId) {
        const { data, error } = await supabase
          .from('clientes_cadastro')
          .select('id, nome_completo')
          .eq('empresa_id', empresaSelecionadaId)
          .order('nome_completo')

        if (error) throw error
        clientesData = data || []
      } else {
        // Para não-administradores, buscar clientes da empresa e também com permissão
        if (userEmpresa?.id || userEmpresa) {
          const empresaId = userEmpresa?.id || userEmpresa
          
          // 1. Buscar clientes da empresa do usuário
          const { data: clientesEmpresa, error: errorEmpresa } = await supabase
            .from('clientes_cadastro')
            .select('id, nome_completo')
            .eq('empresa_id', empresaId)
            .order('nome_completo')

          if (errorEmpresa) throw errorEmpresa
          const clientesEmpresaList = clientesEmpresa || []
          console.log('📋 Clientes da empresa do usuário (OrdemServico):', clientesEmpresaList.length)

          // 2. Buscar permissões de instalações autorizadas
          const { data: permissoes, error: errorPermissoes } = await supabase
            .from('permissoes_instalacoes')
            .select('instalacao_id')
            .eq('empresa_autorizada_id', empresaId)

          if (errorPermissoes) {
            console.error('❌ Erro ao buscar permissões:', errorPermissoes)
          }

          const instalacaoIdsPermitidos = (permissoes || []).map(p => p.instalacao_id)
          console.log('🔐 Instalações autorizadas encontradas (OrdemServico):', instalacaoIdsPermitidos.length)

          // 3. Buscar clientes das instalações autorizadas (que não sejam da própria empresa)
          if (instalacaoIdsPermitidos.length > 0) {
            const { data: clientesPermitidos, error: errorPermitidos } = await supabase
              .from('clientes_cadastro')
              .select('id, nome_completo')
              .in('id', instalacaoIdsPermitidos)
              .neq('empresa_id', empresaId)
              .order('nome_completo')

            if (errorPermitidos) {
              console.error('❌ Erro ao buscar clientes permitidos:', errorPermitidos)
            } else {
              const clientesPermitidosList = clientesPermitidos || []
              console.log('📋 Clientes permitidos encontrados (OrdemServico):', clientesPermitidosList.length)

              // 4. Combinar clientes (usar Map para evitar duplicatas)
              const clientesMap = new Map()
              clientesEmpresaList.forEach(cliente => {
                clientesMap.set(cliente.id, cliente)
              })
              clientesPermitidosList.forEach(cliente => {
                clientesMap.set(cliente.id, cliente)
              })

              clientesData = Array.from(clientesMap.values()).sort((a, b) => {
                return (a.nome_completo || '').localeCompare(b.nome_completo || '')
              })
            }
          } else {
            clientesData = clientesEmpresaList
          }

          console.log('📋 Total de clientes encontrados (OrdemServico, com permissões):', clientesData.length)
        }
      }
      
      setClientes(clientesData)
    } catch (error) {
      console.error('Erro ao carregar clientes:', error)
    }
  }

  // Função para carregar tipos de serviço
  const loadTiposServico = async () => {
    try {
      const { data, error } = await supabase
        .from('tipo_servico')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome')

      if (error) throw error
      setTiposServico(data || [])
    } catch (error) {
      console.error('Erro ao carregar tipos de serviço:', error)
    }
  }

  // Função para carregar usuários (Responsáveis)
  // Empresa selecionada OU usuários com multiempresas = true; + vistoriadores de todas as empresas
  // `empresaIdOverride`: ao editar ordem, usar antes de abrir o modal (evita selects vazios).
  const loadUsuarios = async (empresaIdOverride) => {
    try {
      const empresaId =
        empresaIdOverride !== undefined
          ? empresaIdOverride || null
          : empresaSelecionadaId || null

      // 1) Usuários da empresa OU marcados como multiempresas
      let queryEmpresa = supabase
        .from('usuarios_sistema')
        .select('id, nome')
        .eq('status', 'ativo')
        .order('nome')
      if (empresaId) {
        queryEmpresa = queryEmpresa.or(`empresa_id.eq.${empresaId},multiempresas.eq.true`)
      }
      const { data: dataEmpresa, error: errorEmpresa } = await queryEmpresa
      if (errorEmpresa) throw errorEmpresa
      const usuariosEmpresa = dataEmpresa || []

      // 2) Vistoriadores de todas as empresas (para que qualquer empresa possa selecionar)
      const { data: dataVistoriadores, error: errorVistoriadores } = await supabase
        .from('usuarios_sistema')
        .select('id, nome')
        .eq('status', 'ativo')
        .eq('role', 'vistoriador')
        .order('nome')
      if (errorVistoriadores) throw errorVistoriadores
      const vistoriadores = dataVistoriadores || []

      // Unir listas sem duplicar por id (prioridade: usuários da empresa, depois vistoriadores de outras empresas)
      const idsIncluidos = new Set(usuariosEmpresa.map((u) => u.id))
      const vistoriadoresOutrasEmpresas = vistoriadores.filter((v) => !idsIncluidos.has(v.id))
      const listaFinal = [...usuariosEmpresa, ...vistoriadoresOutrasEmpresas].sort((a, b) =>
        (a.nome || '').localeCompare(b.nome || '')
      )
      setUsuarios(listaFinal)
    } catch (error) {
      console.error('Erro ao carregar usuários:', error)
    }
  }

  // Função para carregar equipes
  const loadEquipes = async (empresaIdOverride) => {
    try {
      let query = supabase
        .from('equipes')
        .select('id, nome')
        .eq('ativo', true)

      let idEmpresaFiltro
      if (empresaIdOverride !== undefined) {
        idEmpresaFiltro = empresaIdOverride || null
      } else if (empresaSelecionadaId) {
        idEmpresaFiltro = empresaSelecionadaId
      } else if (userEmpresa) {
        idEmpresaFiltro = userEmpresa?.id || userEmpresa
      } else {
        idEmpresaFiltro = null
      }

      if (idEmpresaFiltro) {
        query = query.or(`empresa_id.eq.${idEmpresaFiltro},multiequipes.eq.true`)
      }

      const { data, error } = await query.order('nome')

      if (error) throw error
      setEquipes(data || [])
    } catch (error) {
      console.error('Erro ao carregar equipes:', error)
    }
  }

  // Função para abrir modal
  const openModal = () => {
      if (empresaUsuarioId) {
        setEmpresaId(empresaUsuarioId)
      }
      setFormData({
        id_cliente: '',
        id_tipo_servico: '',
        id_equipe: '',
        status: 'Novo',
        obs_operacional: '',
        data_hora_execucao: (() => {
          const d = new Date()
          return `${d.toISOString().split('T')[0]}T08:00`
        })(),
        presenca: false,
        responsaveis: []
      })
    setClienteSearch('')
    setSelectedCliente(null)
    setShowClienteDropdown(false)
    setAnexos([])
    setAnexoTipo(TIPOS_ANEXO_OS[0].value)
    setAnexoErro('')
    setShowModal(true)
  }

  // Função para fechar modal
  const closeModal = () => {
    setShowModal(false)
    setIsEditing(false)
    setEditingOrdem(null)
      setFormData({
        id_cliente: '',
        id_tipo_servico: '',
        id_equipe: '',
        status: 'Novo',
        obs_operacional: '',
        data_hora_execucao: (() => {
          const d = new Date()
          return `${d.toISOString().split('T')[0]}T08:00`
        })(),
        presenca: false,
        responsaveis: []
      })
      setClienteSearch('')
      setSelectedCliente(null)
      setShowClienteDropdown(false)
      setAnexos([])
      setAnexoTipo(TIPOS_ANEXO_OS[0].value)
      setAnexoErro('')
  }

  useEffect(() => {
    if (!showModal) {
      modalAbertoRef.current = false
      return
    }
    if (!empresaSelecionadaId) return

    // Só limpar ao trocar a empresa no dropdown com o modal já aberto (evita apagar dados ao abrir p/ edição ou cliente inicial)
    if (!modalAbertoRef.current) {
      modalAbertoRef.current = true
      empresaNoModalAoAbrirRef.current = empresaSelecionadaId
      return
    }
    if (empresaNoModalAoAbrirRef.current === empresaSelecionadaId) return
    empresaNoModalAoAbrirRef.current = empresaSelecionadaId

    setSelectedCliente(null)
    setClienteSearch('')
    setShowClienteDropdown(false)
    setFormData(prev => ({
      ...prev,
      id_cliente: '',
      id_equipe: '',
      responsaveis: []
    }))
  }, [empresaSelecionadaId, showModal])

  // Funções para searchbox de clientes
  const handleClienteSearch = (value) => {
    setClienteSearch(value)
    setShowClienteDropdown(value.length > 0)
  }

  const handleSelectCliente = (cliente) => {
    setSelectedCliente(cliente)
    setClienteSearch(cliente.nome_completo)
    setShowClienteDropdown(false)
    setFormData(prev => ({
      ...prev,
      id_cliente: cliente.id
    }))
  }

  // Filtrar ordens baseado nos filtros
  const ordensFiltradas = ordensServico.filter(ordem => {
    const clienteMatch = !filtros.cliente || 
      ordem.cliente?.nome_completo?.toLowerCase().includes(filtros.cliente.toLowerCase())
    
    const tipoServicoMatch = !filtros.tipoServico || 
      ordem.tipo_servico?.nome?.toLowerCase().includes(filtros.tipoServico.toLowerCase())
    
    const statusMatch = !filtros.status || 
      ordem.status?.toLowerCase().includes(filtros.status.toLowerCase())
    
    return clienteMatch && tipoServicoMatch && statusMatch
  })

  // Função para lidar com mudanças nos filtros
  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }))
  }

  // Função para normalizar texto (remover acentos)
  const normalizeText = (text) => {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
  }

  // Filtrar clientes baseado na busca
  const filteredClientes = clientes.filter(cliente => {
    if (!cliente.nome_completo) return false
    const searchTerm = normalizeText(clienteSearch)
    const clienteName = normalizeText(cliente.nome_completo)
    return clienteName.includes(searchTerm)
  })

  // Função para lidar com mudanças no formulário
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    const newValue = type === 'checkbox' ? checked : value
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      }
      
      // Se mudou o tipo de serviço, ajustar presença automaticamente
      if (name === 'id_tipo_servico') {
        const tipoSelecionado = tiposServico.find(t => t.id === value)
        if (tipoSelecionado) {
          const nomeTipo = tipoSelecionado.nome.toLowerCase()
          if (nomeTipo.includes('instalação') || nomeTipo.includes('instalacao')) {
            updated.presenca = true
          } else if (nomeTipo.includes('vistoria')) {
            updated.presenca = false
          }
        }
      }
      
      return updated
    })
  }

  // Função para lidar com seleção de responsáveis
  const handleResponsaveisChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value)
    setFormData(prev => ({
      ...prev,
      responsaveis: selectedOptions
    }))
  }

  const handleAnexoFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const validacao = validarArquivoDocumento(file)
    if (!validacao.ok) {
      setAnexoErro(validacao.error)
      e.target.value = ''
      return
    }
    const tipoLabel = TIPOS_ANEXO_OS.find(t => t.value === anexoTipo)?.label || anexoTipo
    setAnexos(prev => [...prev, { id: `${Date.now()}_${prev.length}`, tipo: anexoTipo, tipoLabel, file }])
    setAnexoErro('')
    e.target.value = ''
  }

  const handleRemoverAnexo = (id) => {
    setAnexos(prev => prev.filter(a => a.id !== id))
  }

  /** Envia anexos opcionais vinculados ao cliente (mesmo método do cadastro). */
  const enviarAnexosCliente = async (clienteId, ordemId) => {
    if (!anexos.length || !clienteId) return 0
    let falhas = 0
    for (const anexo of anexos) {
      try {
        await uploadDocumentoCliente({
          clienteId,
          tipoDocumento: anexo.tipo,
          arquivo: anexo.file,
          observacao: `Anexado na OS #${ordemId}`,
          enviadoPor: userId ? String(userId) : 'Sistema'
        })
      } catch (err) {
        console.error('Erro ao enviar anexo da OS:', err)
        falhas += 1
      }
    }
    return falhas
  }

  // Função para editar ordem de serviço
  const handleEditOrdem = async (ordem) => {
    try {
      const normId = (v) => (v == null || v === '' ? '' : String(v).trim())

      const idEmpresaOrdem =
        ordem.id_empresa != null && String(ordem.id_empresa).trim() !== ''
          ? normId(ordem.id_empresa)
          : empresaUsuarioId
            ? normId(empresaUsuarioId)
            : ''

      // Carregar responsáveis da ordem
      const { data: responsaveisData, error: responsaveisError } = await supabase
        .from('ordem_servico_responsaveis')
        .select('id_responsavel')
        .eq('id_ordem_servico', ordem.id)

      if (responsaveisError) throw responsaveisError

      // Buscar evento associado (se existir) para obter data e hora
      const { data: eventoAssociado } = await supabase
        .from('eventos_calendario')
        .select('data_hora')
        .eq('id_ordem_servico', ordem.id)
        .maybeSingle()

      let dataHoraExecucao = ''
      if (eventoAssociado?.data_hora) {
        const d = new Date(eventoAssociado.data_hora)
        const pad = (n) => String(n).padStart(2, '0')
        dataHoraExecucao = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
      } else {
        const dataBase = ordem.data_execucao ? ordem.data_execucao.split('T')[0] : new Date().toISOString().split('T')[0]
        dataHoraExecucao = `${dataBase}T08:00`
      }

      // Equipes e usuários da empresa da ordem antes de abrir o modal (selects precisam das opções na 1ª pintura)
      if (idEmpresaOrdem) {
        await loadEquipes(idEmpresaOrdem)
        await loadUsuarios(idEmpresaOrdem)
      }

      const idEquipeNorm = normId(ordem.id_equipe)
      if (idEquipeNorm) {
        const { data: equipeRow } = await supabase
          .from('equipes')
          .select('id, nome')
          .eq('id', idEquipeNorm)
          .maybeSingle()
        if (equipeRow) {
          setEquipes((prev) => {
            if (prev.some((e) => String(e.id) === String(equipeRow.id))) return prev
            return [...prev, equipeRow].sort((a, b) => (a.nome || '').localeCompare(b.nome || ''))
          })
        }
      }

      const responsaveisIds = (responsaveisData || []).map((r) => normId(r.id_responsavel)).filter(Boolean)
      if (responsaveisIds.length > 0) {
        const { data: usuariosExtra } = await supabase
          .from('usuarios_sistema')
          .select('id, nome')
          .in('id', responsaveisIds)
        if (usuariosExtra?.length) {
          setUsuarios((prev) => {
            const map = new Map(prev.map((u) => [String(u.id), u]))
            usuariosExtra.forEach((u) => map.set(String(u.id), u))
            return Array.from(map.values()).sort((a, b) => (a.nome || '').localeCompare(b.nome || ''))
          })
        }
      }

      // Cliente na lista ou busca direta (ordem de outra empresa / lista ainda filtrada)
      let clienteSelecionado = clientes.find((c) => String(c.id) === String(ordem.id_cliente))
      if (!clienteSelecionado && ordem.id_cliente) {
        const { data: clienteRow, error: errCliente } = await supabase
          .from('clientes_cadastro')
          .select('id, nome_completo')
          .eq('id', ordem.id_cliente)
          .maybeSingle()
        if (!errCliente && clienteRow) clienteSelecionado = clienteRow
      }

      setFormData({
        id_cliente: normId(ordem.id_cliente),
        id_tipo_servico: normId(ordem.id_tipo_servico),
        id_equipe: idEquipeNorm,
        status: ordem.status,
        obs_operacional: ordem.obs_operacional || '',
        data_hora_execucao: dataHoraExecucao,
        presenca: ordem.presenca || false,
        responsaveis: responsaveisIds,
      })

      if (clienteSelecionado) {
        setSelectedCliente(clienteSelecionado)
        setClienteSearch(clienteSelecionado.nome_completo || '')
        setClientes((prev) => {
          const id = clienteSelecionado.id
          if (prev.some((c) => String(c.id) === String(id))) return prev
          return [...prev, clienteSelecionado].sort((a, b) =>
            (a.nome_completo || '').localeCompare(b.nome_completo || '')
          )
        })
      }

      setIsEditing(true)
      setEditingOrdem(ordem)
      if (idEmpresaOrdem) setEmpresaId(idEmpresaOrdem)
      setAnexos([])
      setAnexoTipo(TIPOS_ANEXO_OS[0].value)
      setAnexoErro('')
      setShowModal(true)
    } catch (error) {
      console.error('Erro ao carregar dados para edição:', error)
      showError('Erro ao carregar dados para edição: ' + error.message)
    }
  }

  // Função para cancelar ordem de serviço
  const handleCancelOrdem = (ordem) => {
    setOrdemToDelete(ordem)
    setShowConfirmModal(true)
  }

  // Função para cancelar ordem de serviço
  const confirmCancel = async () => {
    if (!ordemToDelete) return

    try {
      console.log('Cancelando ordem:', ordemToDelete.id)
      
      // Alterar status da ordem para "Cancelado"
      const { error: ordemError } = await supabase
        .from('ordens_servicos')
        .update({
          status: 'Cancelado',
          updated_at: new Date().toISOString()
        })
        .eq('id', ordemToDelete.id)

      if (ordemError) {
        console.error('Erro ao cancelar ordem:', ordemError)
        throw ordemError
      }

      console.log('Ordem cancelada com sucesso')
      showSuccess('Ordem de serviço cancelada com sucesso!')
      setShowConfirmModal(false)
      setOrdemToDelete(null)
      loadOrdensServico() // Recarregar a lista
    } catch (error) {
      console.error('Erro ao cancelar ordem de serviço:', error)
      showError(`Erro ao cancelar ordem de serviço: ${error.message}`)
    }
  }

  // Função para cancelar exclusão
  const cancelDelete = () => {
    setShowConfirmModal(false)
    setOrdemToDelete(null)
  }

  // Função para excluir ordem de serviço (apenas administrador)
  const handleExcluirOrdem = (ordem) => {
    setOrdemToExcluir(ordem)
    setShowExcluirModal(true)
  }

  const confirmExcluirOrdem = async () => {
    if (!ordemToExcluir) return
    try {
      const { error } = await supabase
        .from('ordens_servicos')
        .delete()
        .eq('id', ordemToExcluir.id)
      if (error) throw error
      showSuccess('Ordem de serviço excluída com sucesso!')
      setShowExcluirModal(false)
      setOrdemToExcluir(null)
      loadOrdensServico()
    } catch (err) {
      console.error('Erro ao excluir ordem:', err)
      showError('Erro ao excluir ordem de serviço: ' + (err.message || err))
    }
  }

  // Função para mostrar erro em modal
  const showError = (message) => {
    setErrorMessage(message)
    setShowErrorModal(true)
  }

  // Função para fechar modal de erro
  const closeErrorModal = () => {
    setShowErrorModal(false)
    setErrorMessage('')
  }

  // Função para mostrar sucesso em modal
  const showSuccess = (message) => {
    setSuccessMessage(message)
    setShowSuccessModal(true)
  }

  // Função para fechar modal de sucesso
  const closeSuccessModal = () => {
    setShowSuccessModal(false)
    setSuccessMessage('')
  }

  // Função para mostrar informações da ordem
  const handleShowOrdemInfo = async (ordem) => {
    try {
      // Carregar responsáveis da ordem
      const { data: responsaveisData, error: responsaveisError } = await supabase
        .from('ordem_servico_responsaveis')
        .select(`
          id_responsavel,
          usuario:usuarios_sistema(nome)
        `)
        .eq('id_ordem_servico', ordem.id)

      if (responsaveisError) throw responsaveisError

      // Preparar dados da ordem com responsáveis
      const ordemCompleta = {
        ...ordem,
        responsaveis: responsaveisData || []
      }

      setOrdemInfo(ordemCompleta)
      setShowInfoModal(true)
    } catch (error) {
      console.error('Erro ao carregar informações da ordem:', error)
      showError('Erro ao carregar informações da ordem: ' + error.message)
    }
  }

  // Função para fechar modal de informações
  const closeInfoModal = () => {
    setShowInfoModal(false)
    setOrdemInfo(null)
  }

  // Verificar conflito: mesma equipe não pode ter ordem agendada no mesmo horário
  const verificarConflitoDataHora = async () => {
    if (!formData.data_hora_execucao || !formData.id_equipe) return false
    const dataHora = new Date(formData.data_hora_execucao)
    const start = new Date(dataHora)
    start.setSeconds(0, 0)
    const end = new Date(dataHora)
    end.setSeconds(59, 999)
    let q = supabase
      .from('eventos_calendario')
      .select('id, id_ordem_servico')
      .not('id_ordem_servico', 'is', null)
      .gte('data_hora', start.toISOString())
      .lte('data_hora', end.toISOString())
    if (isEditing && editingOrdem) {
      q = q.neq('id_ordem_servico', editingOrdem.id)
    }
    const { data: eventos, error } = await q
    if (error) throw error
    if (!eventos || eventos.length === 0) return false
    const ordemIds = [...new Set(eventos.map((e) => e.id_ordem_servico).filter(Boolean))]
    const { data: ordensMesmaEquipe } = await supabase
      .from('ordens_servicos')
      .select('id')
      .in('id', ordemIds)
      .eq('id_equipe', formData.id_equipe)
    return (ordensMesmaEquipe && ordensMesmaEquipe.length > 0)
  }

  // Verificar conflito: Instalação só pode ser agendada uma por dia para a mesma equipe (exceto se cancelada ou finalizada)
  const verificarConflitoInstalacaoEquipe = async () => {
    const tipoSelecionado = tiposServico.find(t => t.id === formData.id_tipo_servico)
    if (!tipoSelecionado) return false
    const nomeTipo = (tipoSelecionado.nome || '').toLowerCase()
    if (!nomeTipo.includes('instalação') && !nomeTipo.includes('instalacao')) return false
    if (!formData.id_equipe) return false
    const dataExecucao = formData.data_hora_execucao ? formData.data_hora_execucao.split('T')[0] : null
    if (!dataExecucao) return false

    // Só bloqueia se existir outra ordem de Instalação na mesma equipe/data com status ativo (Novo, Em andamento, Validado)
    let q = supabase
      .from('ordens_servicos')
      .select('id')
      .eq('id_tipo_servico', formData.id_tipo_servico)
      .eq('id_equipe', formData.id_equipe)
      .eq('data_execucao', dataExecucao)
      .in('status', ['Novo', 'Em andamento', 'Validado'])
    if (isEditing && editingOrdem) {
      q = q.neq('id', editingOrdem.id)
    }
    const { data, error } = await q
    if (error) throw error
    return (data && data.length > 0)
  }

  // Função para salvar ordem de serviço
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.data_hora_execucao) {
      showError('Data e hora de execução são obrigatórios.')
      return
    }
    try {
      const conflito = await verificarConflitoDataHora()
      if (conflito) {
        setShowConflitoModal(true)
        return
      }
      const conflitoInstalacao = await verificarConflitoInstalacaoEquipe()
      if (conflitoInstalacao) {
        setShowConflitoInstalacaoModal(true)
        return
      }
    } catch (err) {
      console.error('Erro ao verificar conflito:', err)
      showError('Erro ao verificar disponibilidade: ' + (err.message || err))
      return
    }
    setIsLoading(true)

    try {
      if (isEditing && editingOrdem) {
        // Atualizar ordem existente
        const dataExecucao = formData.data_hora_execucao ? formData.data_hora_execucao.split('T')[0] : null
        const { error: ordemError } = await supabase
          .from('ordens_servicos')
          .update({
            id_cliente: formData.id_cliente,
            id_tipo_servico: formData.id_tipo_servico,
            id_equipe: formData.id_equipe || null,
            status: formData.status,
            obs_operacional: formData.obs_operacional,
            data_execucao: dataExecucao,
            presenca: formData.presenca,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingOrdem.id)

        if (ordemError) throw ordemError

        // Remover responsáveis existentes
        const { error: deleteResponsaveisError } = await supabase
          .from('ordem_servico_responsaveis')
          .delete()
          .eq('id_ordem_servico', editingOrdem.id)

        if (deleteResponsaveisError) throw deleteResponsaveisError

        // Adicionar novos responsáveis se houver
        if (formData.responsaveis.length > 0) {
          const responsaveisData = formData.responsaveis.map(id_responsavel => ({
            id_ordem_servico: editingOrdem.id,
            id_responsavel: id_responsavel
          }))

          const { error: responsaveisError } = await supabase
            .from('ordem_servico_responsaveis')
            .insert(responsaveisData)

          if (responsaveisError) throw responsaveisError
        }

        // Atualizar ou criar evento no calendário associado à ordem de serviço
        if (userId && formData.data_hora_execucao) {
          try {
            const clienteNome = clientes.find((c) => c.id === formData.id_cliente)?.nome_completo || 'Cliente'
            const tipoNome = tiposServico.find((t) => t.id === formData.id_tipo_servico)?.nome || 'Serviço'
            const nomeEvento = `OS #${editingOrdem.id} - ${clienteNome} (${tipoNome})`
            const dataHora = new Date(formData.data_hora_execucao).toISOString()
            const obsEvento = `Criada a partir da Ordem de Serviço #${editingOrdem.id}`

            const { data: eventoExistente } = await supabase
              .from('eventos_calendario')
              .select('id')
              .eq('id_ordem_servico', editingOrdem.id)
              .maybeSingle()

            if (eventoExistente) {
              // Atualizar evento existente
              const { error: updateEvErr } = await supabase
                .from('eventos_calendario')
                .update({
                  nome_evento: nomeEvento,
                  tipo_evento: 'operacional',
                  executado: formData.status === 'Finalizado',
                  data_hora: dataHora,
                  id_responsavel: userId,
                  observacao: obsEvento
                })
                .eq('id', eventoExistente.id)
              if (updateEvErr) throw updateEvErr

              // Atualizar usuários do evento
              await supabase.from('eventos_calendario_usuarios').delete().eq('evento_id', eventoExistente.id)
              if (formData.responsaveis?.length > 0) {
                const usuariosRows = formData.responsaveis.map((usuario_id) => ({
                  evento_id: eventoExistente.id,
                  usuario_id
                }))
                await supabase.from('eventos_calendario_usuarios').insert(usuariosRows)
              }
              await sincronizarEventoGoogleCalendar({ usuario_id: userId, evento_id: eventoExistente.id, op: 'upsert' })
            } else {
              // Criar novo evento (ordem não tinha evento associado)
              const payloadEvento = {
                nome_evento: nomeEvento,
                tipo_evento: 'operacional',
                executado: formData.status === 'Finalizado',
                data_hora: dataHora,
                id_responsavel: userId,
                observacao: obsEvento,
                id_ordem_servico: editingOrdem.id
              }
              const { data: eventoData, error: eventoError } = await supabase
                .from('eventos_calendario')
                .insert([payloadEvento])
                .select('id')
              if (eventoError) throw eventoError
              if (eventoData?.[0]?.id && formData.responsaveis?.length > 0) {
                const usuariosRows = formData.responsaveis.map((usuario_id) => ({
                  evento_id: eventoData[0].id,
                  usuario_id
                }))
                await supabase.from('eventos_calendario_usuarios').insert(usuariosRows)
              }
              if (eventoData?.[0]?.id) {
                await sincronizarEventoGoogleCalendar({ usuario_id: userId, evento_id: eventoData[0].id, op: 'upsert' })
              }
            }
          } catch (evErr) {
            console.warn('Evento no calendário não atualizado/criado:', evErr)
          }
        }

        const falhasAnexosEdit = await enviarAnexosCliente(formData.id_cliente, editingOrdem.id)
        if (falhasAnexosEdit > 0) {
          showSuccess(`Ordem atualizada, mas ${falhasAnexosEdit} anexo(s) não puderam ser enviados.`)
        } else {
          showSuccess('Ordem de serviço atualizada com sucesso!')
        }
      } else {
        // Criar nova ordem de serviço
        const dataExecucao = formData.data_hora_execucao ? formData.data_hora_execucao.split('T')[0] : null
        const payloadOrdem = {
          id_cliente: formData.id_cliente,
          id_tipo_servico: formData.id_tipo_servico,
          id_equipe: formData.id_equipe || null,
          id_empresa: empresaSelecionadaId,
          status: formData.status,
          obs_operacional: formData.obs_operacional,
          data_execucao: dataExecucao,
          presenca: formData.presenca
        }
        const { data: ordemData, error: ordemError } = await supabase
          .from('ordens_servicos')
          .insert([payloadOrdem])
          .select()

        if (ordemError) throw ordemError

        // Adicionar responsáveis se houver
        if (formData.responsaveis.length > 0) {
          const responsaveisData = formData.responsaveis.map(id_responsavel => ({
            id_ordem_servico: ordemData[0].id,
            id_responsavel: id_responsavel
          }))

          const { error: responsaveisError } = await supabase
            .from('ordem_servico_responsaveis')
            .insert(responsaveisData)

          if (responsaveisError) throw responsaveisError
        }

        // Criar evento no calendário associado à ordem de serviço
        if (userId && formData.data_hora_execucao) {
          try {
            const clienteNome = clientes.find((c) => c.id === formData.id_cliente)?.nome_completo || 'Cliente'
            const tipoNome = tiposServico.find((t) => t.id === formData.id_tipo_servico)?.nome || 'Serviço'
            const nomeEvento = `OS #${ordemData[0].id} - ${clienteNome} (${tipoNome})`
            const dataHora = new Date(formData.data_hora_execucao).toISOString()
            const obsEvento = `Criada a partir da Ordem de Serviço #${ordemData[0].id}`

            const payloadEvento = {
              nome_evento: nomeEvento,
              tipo_evento: 'operacional',
              executado: formData.status === 'Finalizado',
              data_hora: dataHora,
              id_responsavel: userId,
              observacao: obsEvento
            }
            const { data: eventoData, error: eventoError } = await supabase
              .from('eventos_calendario')
              .insert([{ ...payloadEvento, id_ordem_servico: ordemData[0].id }])
              .select('id')

            if (eventoError) throw eventoError
            if (eventoData?.[0]?.id && formData.responsaveis?.length > 0) {
              const usuariosRows = formData.responsaveis.map((usuario_id) => ({
                evento_id: eventoData[0].id,
                usuario_id
              }))
              await supabase.from('eventos_calendario_usuarios').insert(usuariosRows)
            }
            if (eventoData?.[0]?.id) {
              await sincronizarEventoGoogleCalendar({ usuario_id: userId, evento_id: eventoData[0].id, op: 'upsert' })
            }
          } catch (evErr) {
            console.warn('Evento no calendário não criado (execute migração 81 se necessário):', evErr)
          }
        }

        const falhasAnexos = await enviarAnexosCliente(formData.id_cliente, ordemData[0].id)
        if (falhasAnexos > 0) {
          showSuccess(`Ordem criada, mas ${falhasAnexos} anexo(s) não puderam ser enviados.`)
        } else {
          showSuccess('Ordem de serviço criada com sucesso!')
        }
      }

      closeModal()
      
      // Aguardar um pouco para garantir que o banco commitou a transação
      await new Promise(resolve => setTimeout(resolve, 300))
      
      // Recarregar a lista
      await loadOrdensServico()
    } catch (error) {
      console.error('Erro ao salvar ordem de serviço:', error)
      showError('Erro ao salvar ordem de serviço: ' + error.message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="menu-content">
      <div className="menu-header">
        <h2>Ordem de Serviços</h2>
        <button onClick={onBack} className="back-button">
          ← Voltar ao Dashboard
        </button>
      </div>

      <div className="ordem-actions">
        <button onClick={openModal} className="action-button primary">
          ➕ Nova Ordem de Serviço
        </button>
      </div>

      {/* Filtros de Pesquisa */}
      <div className="filtros-container">
        <h3>🔍 Filtros de Pesquisa</h3>
        <div className="filtros-grid">
          <div className="filtro-item">
            <label htmlFor="filtro-cliente">Cliente:</label>
            <input
              type="text"
              id="filtro-cliente"
              value={filtros.cliente}
              onChange={(e) => handleFiltroChange('cliente', e.target.value)}
              placeholder="Digite o nome do cliente"
            />
          </div>
          
          <div className="filtro-item">
            <label htmlFor="filtro-tipo-servico">Tipo de Serviço:</label>
            <input
              type="text"
              id="filtro-tipo-servico"
              value={filtros.tipoServico}
              onChange={(e) => handleFiltroChange('tipoServico', e.target.value)}
              placeholder="Digite o tipo de serviço"
            />
          </div>
          
          <div className="filtro-item">
            <label htmlFor="filtro-status">Status:</label>
            <select
              id="filtro-status"
              value={filtros.status}
              onChange={(e) => handleFiltroChange('status', e.target.value)}
            >
              <option value="">Todos os status</option>
              <option value="Novo">Novo</option>
              <option value="Em andamento">Em andamento</option>
              <option value="Finalizado">Finalizado</option>
              <option value="Validado">Validado</option>
              <option value="Cancelado">Cancelado</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Ordens de Serviço */}
      <div className="ordens-list">
        <h3>Ordens de Serviço ({ordensFiltradas.length} de {ordensServico.length})</h3>
        
        {isLoadingOrdens ? (
          <div className="loading-message">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            Carregando ordens de serviço...
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Tipo de Serviço</th>
                  <th>Equipe</th>
                  <th>Status</th>
                  <th>Data de Execução</th>
                  <th>Data de Criação</th>
                  <th>Observações</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {ordensFiltradas.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-data">
                      {ordensServico.length === 0 ? 'Nenhuma ordem de serviço cadastrada ainda.' : 'Nenhuma ordem encontrada com os filtros aplicados.'}
                    </td>
                  </tr>
                ) : (
                  ordensFiltradas.map((ordem) => (
                    <tr key={ordem.id}>
                      <td>
                        <button 
                          className="cliente-button"
                          onClick={() => handleShowOrdemInfo(ordem)}
                          title="Clique para ver informações da ordem"
                        >
                          {ordem.cliente?.nome_completo || 'N/A'}
                        </button>
                      </td>
                      <td>{ordem.tipo_servico?.nome || 'N/A'}</td>
                      <td>{ordem.equipe?.nome || 'N/A'}</td>
                      <td>
                        <span className={`status-badge status-${ordem.status?.toLowerCase().replace(' ', '-') || 'novo'}`}>
                          {ordem.status === 'Novo' ? '🆕 Novo' : 
                           ordem.status === 'Em andamento' ? '🔄 Em Andamento' : 
                           ordem.status === 'Finalizado' ? '✅ Finalizado' : 
                           ordem.status === 'Validado' ? '🔍 Validado' : 
                           ordem.status === 'Cancelado' ? '❌ Cancelado' : 
                           '🆕 Novo'}
                        </span>
                      </td>
                      <td>{ordem.data_execucao ? formatarDataSemHora(ordem.data_execucao) : 'N/A'}</td>
                      <td>{ordem.created_at ? new Date(ordem.created_at).toLocaleDateString('pt-BR') : 'N/A'}</td>
                      <td>{ordem.obs_operacional ? (ordem.obs_operacional.length > 50 ? ordem.obs_operacional.substring(0, 50) + '...' : ordem.obs_operacional) : 'N/A'}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="action-btn edit"
                            onClick={() => handleEditOrdem(ordem)}
                            title="Editar ordem de serviço"
                          >
                            ✏️ Editar
                          </button>
                          {ordem.status !== 'Cancelado' && (
                            <button 
                              className="action-btn delete"
                              onClick={() => handleCancelOrdem(ordem)}
                              title="Cancelar ordem de serviço"
                            >
                              ❌ Cancelar
                            </button>
                          )}
                          {userRole === 'administrador' && (
                            <button 
                              className="action-btn delete"
                              onClick={() => handleExcluirOrdem(ordem)}
                              title="Excluir ordem de serviço"
                            >
                              🗑️ Excluir
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal do formulário */}
      {showModal && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          width: '100vw',
          height: '100vh',
          padding: '20px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            position: 'relative',
            zIndex: 1000000,
            animation: 'slideUp 0.3s ease-out'
          }}>
            {/* Header do Modal */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '24px 30px',
              borderRadius: '16px 16px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
                  {isEditing ? 'Editar Ordem de Serviço' : 'Nova Ordem de Serviço'}
                </h3>
                <p style={{ margin: '4px 0 0 0', opacity: 0.9, fontSize: '0.9rem' }}>
                  {isEditing ? 'Edite os dados da ordem de serviço' : 'Preencha os dados para criar uma nova ordem'}
                </p>
              </div>
              <button 
                onClick={closeModal} 
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  color: 'white',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                title="Fechar"
              >
                ×
              </button>
            </div>
            
            {/* Formulário */}
            <form onSubmit={handleSubmit} style={{ padding: '30px' }}>
              {/* Campo Empresa */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Empresa
                </label>
                <select
                  value={empresaSelecionadaId || ''}
                  onChange={(e) => {
                    if (podeSelecionarEmpresaNaOS) setEmpresaId(e.target.value)
                  }}
                  disabled={!podeSelecionarEmpresaNaOS}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    cursor: podeSelecionarEmpresaNaOS ? 'pointer' : 'not-allowed',
                    opacity: podeSelecionarEmpresaNaOS ? 1 : 0.9
                  }}
                >
                  {podeSelecionarEmpresaNaOS
                    ? empresasDropdownList.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.empresa}
                        </option>
                      ))
                    : empresaUsuarioId ? (
                        <option value={empresaUsuarioId}>{userEmpresa?.empresa || 'Empresa do usuário'}</option>
                      ) : (
                        <option value="">Empresa não definida</option>
                      )}
                </select>
                {!podeSelecionarEmpresaNaOS && userEmpresa?.empresa && (
                  <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>Vinculado à sua empresa</small>
                )}
              </div>

              {/* Campo Cliente com Searchbox */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Cliente *
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={clienteSearch}
                    onChange={(e) => handleClienteSearch(e.target.value)}
                    placeholder="Digite o nome do cliente..."
                    required
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      border: '2px solid #e5e7eb', 
                      borderRadius: '8px', 
                      fontSize: '16px', 
                      boxSizing: 'border-box',
                      transition: 'all 0.3s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea'
                      if (clienteSearch.length > 0) {
                        setShowClienteDropdown(true)
                      }
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb'
                      // Delay para permitir clique na sugestão
                      setTimeout(() => {
                        setShowClienteDropdown(false)
                      }, 200)
                    }}
                  />
                  {showClienteDropdown && filteredClientes.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      maxHeight: '300px',
                      overflowY: 'auto',
                      marginTop: '4px'
                    }}>
                      {filteredClientes.map((cliente) => (
                        <div
                          key={cliente.id}
                          onClick={() => handleSelectCliente(cliente)}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f3f4f6',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#f9fafb'}
                          onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <div style={{ fontWeight: '500', color: '#111827' }}>{cliente.nome_completo}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Campo Tipo de Serviço */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Tipo de Serviço *
                </label>
                <select
                  name="id_tipo_servico"
                  value={formData.id_tipo_servico}
                  onChange={handleInputChange}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '12px 16px', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '8px', 
                    fontSize: '16px', 
                    boxSizing: 'border-box',
                    transition: 'all 0.3s ease',
                    outline: 'none',
                    backgroundColor: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  <option value="">Selecione um tipo de serviço</option>
                  {tiposServico.map(tipo => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nome}
                    </option>
                  ))}
                </select>
              </div>

              {/* Campo Equipe */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Equipe *
                </label>
                <select
                  name="id_equipe"
                  value={formData.id_equipe}
                  onChange={handleInputChange}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '12px 16px', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '8px', 
                    fontSize: '16px', 
                    boxSizing: 'border-box',
                    transition: 'all 0.3s ease',
                    outline: 'none',
                    backgroundColor: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  <option value="">Selecione uma equipe</option>
                  {equipes.map(equipe => (
                    <option key={equipe.id} value={equipe.id}>
                      {equipe.nome}
                    </option>
                  ))}
                </select>
              </div>

              {/* Campo Status */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  style={{ 
                    width: '100%', 
                    padding: '12px 16px', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '8px', 
                    fontSize: '16px', 
                    boxSizing: 'border-box',
                    transition: 'all 0.3s ease',
                    outline: 'none',
                    backgroundColor: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  <option value="Novo">Novo</option>
                  <option value="Em andamento">Em andamento</option>
                  <option value="Finalizado">Finalizado</option>
                  <option value="Validado">Validado</option>
                  <option value="Cancelado">Cancelado</option>
                </select>
              </div>

              {/* Campo Data e Hora de Execução */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Data e Hora de Execução
                </label>
                <input
                  type="datetime-local"
                  name="data_hora_execucao"
                  value={formData.data_hora_execucao}
                  onChange={handleInputChange}
                  style={{ 
                    width: '100%', 
                    padding: '12px 16px', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '8px', 
                    fontSize: '16px', 
                    boxSizing: 'border-box',
                    transition: 'all 0.3s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              {/* Campo Presença */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  gap: '12px',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  backgroundColor: formData.presenca ? '#ecfdf5' : 'white'
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = '#667eea'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                >
                  <input
                    type="checkbox"
                    name="presenca"
                    checked={formData.presenca}
                    onChange={handleInputChange}
                    style={{ 
                      width: '20px', 
                      height: '20px', 
                      cursor: 'pointer',
                      accentColor: '#667eea'
                    }}
                  />
                  <span style={{ 
                    fontWeight: '600', 
                    color: '#374151',
                    fontSize: '14px',
                    userSelect: 'none'
                  }}>
                    Presença
                  </span>
                </label>
                <small style={{ 
                  fontSize: '12px', 
                  color: '#6b7280', 
                  marginTop: '4px',
                  display: 'block',
                  marginLeft: '32px'
                }}>
                  {formData.id_tipo_servico ? (
                    tiposServico.find(t => t.id === formData.id_tipo_servico)?.nome.toLowerCase().includes('instalação') || 
                    tiposServico.find(t => t.id === formData.id_tipo_servico)?.nome.toLowerCase().includes('instalacao') 
                      ? 'Marcado automaticamente para Instalação' 
                      : tiposServico.find(t => t.id === formData.id_tipo_servico)?.nome.toLowerCase().includes('vistoria')
                        ? 'Desmarcado automaticamente para Vistoria'
                        : ''
                  ) : 'O estado será ajustado automaticamente ao selecionar o Tipo de Serviço'}
                </small>
              </div>

              {/* Campo Observações Operacionais */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Observações Operacionais
                </label>
                <textarea
                  name="obs_operacional"
                  value={formData.obs_operacional}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Observações para a equipe operacional..."
                  style={{ 
                    width: '100%', 
                    padding: '12px 16px', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '8px', 
                    fontSize: '16px', 
                    boxSizing: 'border-box',
                    transition: 'all 0.3s ease',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: '80px'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              {/* Campo Anexos (opcional) — visível para todos com acesso à tela */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Anexos (opcional)
                </label>
                <small style={{ display: 'block', marginBottom: '10px', color: '#6b7280', fontSize: '12px' }}>
                  Unifilar, Análise de Sombreamento ou Vistoria. Os arquivos ficam no cadastro do cliente. (PDF, JPG, PNG, WebP — máx. 20MB)
                </small>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <select
                    value={anexoTipo}
                    onChange={(e) => setAnexoTipo(e.target.value)}
                    style={{ flex: '1 1 200px', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                  >
                    {TIPOS_ANEXO_OS.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <input
                    type="file"
                    onChange={handleAnexoFileChange}
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    style={{ flex: '1 1 240px', padding: '8px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                {anexoErro && (
                  <small style={{ display: 'block', marginTop: '6px', color: '#b91c1c', fontSize: '12px' }}>{anexoErro}</small>
                )}
                {anexos.length > 0 && (
                  <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {anexos.map(anexo => (
                      <div
                        key={anexo.id}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
                          padding: '8px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px'
                        }}
                      >
                        <span style={{ fontSize: '13px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <strong>{anexo.tipoLabel}:</strong> {anexo.file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoverAnexo(anexo.id)}
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '18px', lineHeight: 1, flexShrink: 0 }}
                          title="Remover anexo"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Campo Responsáveis */}
              <div style={{ marginBottom: '32px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Responsáveis
                </label>
                <select
                  name="responsaveis"
                  multiple
                  value={formData.responsaveis}
                  onChange={handleResponsaveisChange}
                  style={{ 
                    width: '100%', 
                    padding: '12px 16px', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '8px', 
                    fontSize: '16px', 
                    boxSizing: 'border-box',
                    transition: 'all 0.3s ease',
                    outline: 'none',
                    backgroundColor: 'white',
                    minHeight: '120px'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  {usuarios.map(usuario => (
                    <option key={usuario.id} value={usuario.id}>
                      {usuario.nome}
                    </option>
                  ))}
                </select>
                <small style={{ 
                  fontSize: '12px', 
                  color: '#6b7280', 
                  marginTop: '4px',
                  display: 'block'
                }}>
                  Pressione Ctrl (ou Cmd no Mac) para selecionar múltiplos responsáveis
                </small>
              </div>

              {/* Botões de Ação */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '12px', 
                marginTop: '32px', 
                paddingTop: '24px', 
                borderTop: '1px solid #e5e7eb' 
              }}>
                <button 
                  type="button" 
                  onClick={closeModal} 
                  style={{ 
                    padding: '12px 24px', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '8px', 
                    cursor: 'pointer', 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    backgroundColor: 'white', 
                    color: '#374151',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = '#d1d5db'
                    e.target.style.backgroundColor = '#f9fafb'
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = '#e5e7eb'
                    e.target.style.backgroundColor = 'white'
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  disabled={isLoading || !formData.id_cliente || !formData.id_tipo_servico}
                  style={{ 
                    padding: '12px 24px', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: isLoading || !formData.id_cliente || !formData.id_tipo_servico ? 'not-allowed' : 'pointer', 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    background: isLoading || !formData.id_cliente || !formData.id_tipo_servico ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    transition: 'all 0.3s ease',
                    opacity: isLoading || !formData.id_cliente || !formData.id_tipo_servico ? 0.6 : 1
                  }}
                  onMouseOver={(e) => {
                    if (!isLoading && formData.id_cliente && formData.id_tipo_servico) {
                      e.target.style.transform = 'translateY(-1px)'
                      e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)'
                    }
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)'
                    e.target.style.boxShadow = 'none'
                  }}
                >
                  {isLoading ? 'Salvando...' : (isEditing ? 'Atualizar Ordem' : 'Criar Ordem')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showConfirmModal && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          width: '100vw',
          height: '100vh',
          padding: '20px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            position: 'relative',
            zIndex: 1000000,
            animation: 'slideUp 0.3s ease-out'
          }}>
            {/* Header do Modal */}
            <div style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              padding: '24px 30px',
              borderRadius: '16px 16px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>⚠️ Confirmar Cancelamento</h3>
                <p style={{ margin: '4px 0 0 0', opacity: 0.9, fontSize: '0.9rem' }}>A ordem será marcada como cancelada</p>
              </div>
              <button 
                onClick={cancelDelete} 
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  color: 'white',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                title="Fechar"
              >
                ×
              </button>
            </div>
            
            {/* Conteúdo do Modal */}
            <div style={{ padding: '30px' }}>
              <div style={{ marginBottom: '24px' }}>
                <p style={{ fontSize: '16px', color: '#374151', marginBottom: '16px' }}>
                  Tem certeza que deseja cancelar esta ordem de serviço?
                </p>
                <div style={{ 
                  background: '#fef2f2', 
                  border: '1px solid #fecaca', 
                  borderRadius: '8px', 
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#991b1b' }}>
                    <strong>Cliente:</strong> {ordemToDelete?.cliente?.nome_completo || 'N/A'}<br/>
                    <strong>Tipo de Serviço:</strong> {ordemToDelete?.tipo_servico?.nome || 'N/A'}<br/>
                    <strong>Status Atual:</strong> {ordemToDelete?.status || 'N/A'}
                  </p>
                </div>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                  A ordem será marcada como "Cancelado" e não será mais processada. Esta ação pode ser revertida editando a ordem.
                </p>
              </div>

              {/* Botões de Ação */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '12px'
              }}>
                <button 
                  onClick={cancelDelete}
                  style={{ 
                    padding: '12px 24px', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '8px', 
                    cursor: 'pointer', 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    backgroundColor: 'white', 
                    color: '#374151',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = '#d1d5db'
                    e.target.style.backgroundColor = '#f9fafb'
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = '#e5e7eb'
                    e.target.style.backgroundColor = 'white'
                  }}
                >
                  Cancelar
                </button>
                <button 
                  onClick={confirmCancel}
                  style={{ 
                    padding: '12px 24px', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: 'pointer', 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-1px)'
                    e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)'
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)'
                    e.target.style.boxShadow = 'none'
                  }}
                >
                  ❌ Cancelar Ordem
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Confirmação de Exclusão (apenas administrador) */}
      {showExcluirModal && ordemToExcluir && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999999,
            padding: '20px'
          }}
          onClick={() => { setShowExcluirModal(false); setOrdemToExcluir(null) }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              color: 'white',
              padding: '24px 30px'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>🗑️ Excluir Ordem de Serviço</h3>
              <p style={{ margin: '4px 0 0 0', opacity: 0.9, fontSize: '0.9rem' }}>Esta ação não pode ser desfeita</p>
            </div>
            <div style={{ padding: '24px 30px' }}>
              <p style={{ margin: '0 0 16px', fontSize: '1rem', color: '#374151' }}>
                Tem certeza que deseja excluir permanentemente esta ordem de serviço?
              </p>
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#991b1b' }}>
                  <strong>Cliente:</strong> {ordemToExcluir?.cliente?.nome_completo || 'N/A'}<br/>
                  <strong>Tipo:</strong> {ordemToExcluir?.tipo_servico?.nome || 'N/A'}<br/>
                  <strong>OS #</strong>{ordemToExcluir?.id}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setShowExcluirModal(false); setOrdemToExcluir(null) }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    background: '#f9fafb',
                    color: '#374151',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmExcluirOrdem}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    color: 'white',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  🗑️ Excluir
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Informações da Ordem */}
      {showInfoModal && ordemInfo && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          width: '100vw',
          height: '100vh',
          padding: '20px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            position: 'relative',
            zIndex: 1000000,
            animation: 'slideUp 0.3s ease-out'
          }}>
            {/* Header do Modal */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '24px 30px',
              borderRadius: '16px 16px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
                  📋 Ordem de Serviço #{ordemInfo.id}
                </h3>
                <p style={{ margin: '4px 0 0 0', opacity: 0.9, fontSize: '0.9rem' }}>
                  Detalhes completos da ordem de serviço
                </p>
              </div>
              <button 
                onClick={closeInfoModal} 
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  color: 'white',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                title="Fechar"
              >
                ×
              </button>
            </div>
            
            {/* Conteúdo do Modal */}
            <div style={{ padding: '30px' }}>
              {/* Informações Principais */}
              <div style={{ marginBottom: '32px' }}>
                <h4 style={{ color: '#374151', marginBottom: '16px', fontSize: '18px' }}>📊 Dados Principais</h4>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                  gap: '16px' 
                }}>
                  <div style={{ 
                    background: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '16px' 
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b' }}>Cliente</p>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                      {ordemInfo.cliente?.nome_completo || 'N/A'}
                    </p>
                  </div>
                  <div style={{ 
                    background: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '16px' 
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b' }}>Tipo de Serviço</p>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                      {ordemInfo.tipo_servico?.nome || 'N/A'}
                    </p>
                  </div>
                  <div style={{ 
                    background: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '16px' 
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b' }}>Equipe</p>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                      {ordemInfo.equipe?.nome || 'N/A'}
                    </p>
                  </div>
                  <div style={{ 
                    background: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '16px' 
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b' }}>Status</p>
                    <span className={`status-badge status-${ordemInfo.status?.toLowerCase().replace(' ', '-') || 'novo'}`}>
                      {ordemInfo.status === 'Novo' ? '🆕 Novo' : 
                       ordemInfo.status === 'Em andamento' ? '🔄 Em Andamento' : 
                       ordemInfo.status === 'Finalizado' ? '✅ Finalizado' : 
                       ordemInfo.status === 'Validado' ? '🔍 Validado' : 
                       ordemInfo.status === 'Cancelado' ? '❌ Cancelado' : 
                       '🆕 Novo'}
                    </span>
                  </div>
                  <div style={{ 
                    background: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '16px' 
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b' }}>Data de Execução</p>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                      {formatarDataSemHora(ordemInfo.data_execucao)}
                    </p>
                  </div>
                  <div style={{ 
                    background: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '16px' 
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b' }}>Data de Criação</p>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                      {ordemInfo.created_at ? new Date(ordemInfo.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                    </p>
                  </div>
                  <div style={{ 
                    background: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '16px' 
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b' }}>Última Atualização</p>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                      {ordemInfo.updated_at ? new Date(ordemInfo.updated_at).toLocaleDateString('pt-BR') : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Observações */}
              {ordemInfo.obs_operacional && (
                <div style={{ marginBottom: '32px' }}>
                  <h4 style={{ color: '#374151', marginBottom: '16px', fontSize: '18px' }}>📝 Observações Operacionais</h4>
                  <div style={{ 
                    background: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '16px' 
                  }}>
                    <p style={{ margin: 0, fontSize: '16px', color: '#1e293b', lineHeight: '1.6' }}>
                      {ordemInfo.obs_operacional}
                    </p>
                  </div>
                </div>
              )}

              {/* Responsáveis */}
              <div style={{ marginBottom: '32px' }}>
                <h4 style={{ color: '#374151', marginBottom: '16px', fontSize: '18px' }}>👥 Responsáveis</h4>
                {ordemInfo.responsaveis && ordemInfo.responsaveis.length > 0 ? (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                    gap: '12px' 
                  }}>
                    {ordemInfo.responsaveis.map((responsavel, index) => (
                      <div key={index} style={{ 
                        background: '#f0f9ff', 
                        border: '1px solid #bae6fd', 
                        borderRadius: '8px', 
                        padding: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '20px' }}>👤</span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#0369a1' }}>
                          {responsavel.usuario?.nome || 'Usuário não encontrado'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    background: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
                      Nenhum responsável atribuído a esta ordem
                    </p>
                  </div>
                )}
              </div>

              {/* Botão de Fechar */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                paddingTop: '24px', 
                borderTop: '1px solid #e5e7eb' 
              }}>
                <button 
                  onClick={closeInfoModal}
                  style={{ 
                    padding: '12px 32px', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: 'pointer', 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-1px)'
                    e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)'
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)'
                    e.target.style.boxShadow = 'none'
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Erro */}
      {showErrorModal && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          width: '100vw',
          height: '100vh',
          padding: '20px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            position: 'relative',
            zIndex: 1000000,
            animation: 'slideUp 0.3s ease-out'
          }}>
            {/* Header do Modal */}
            <div style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              padding: '24px 30px',
              borderRadius: '16px 16px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>❌ Erro</h3>
                <p style={{ margin: '4px 0 0 0', opacity: 0.9, fontSize: '0.9rem' }}>Ocorreu um problema</p>
              </div>
              <button 
                onClick={closeErrorModal} 
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  color: 'white',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                title="Fechar"
              >
                ×
              </button>
            </div>
            
            {/* Conteúdo do Modal */}
            <div style={{ padding: '30px' }}>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ 
                  background: '#fef2f2', 
                  border: '1px solid #fecaca', 
                  borderRadius: '8px', 
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#991b1b', lineHeight: '1.5' }}>
                    {errorMessage}
                  </p>
                </div>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                  Verifique os dados e tente novamente. Se o problema persistir, entre em contato com o suporte técnico.
                </p>
              </div>

              {/* Botão de Fechar */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center'
              }}>
                <button 
                  onClick={closeErrorModal}
                  style={{ 
                    padding: '12px 32px', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: 'pointer', 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-1px)'
                    e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)'
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)'
                    e.target.style.boxShadow = 'none'
                  }}
                >
                  Entendi
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modais de validação - renderizados via portal no body para ficarem acima do modal de formulário */}
      {showConflitoModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999999,
            padding: '20px'
          }}
          onClick={() => setShowConflitoModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              padding: '32px 24px',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⚠️</div>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.5rem', color: '#333' }}>Horário indisponível para esta equipe</h3>
            <p style={{ margin: '0 0 24px', fontSize: '1.1rem', color: '#666', lineHeight: 1.6 }}>
              Já existe uma ordem de serviço agendada para a mesma equipe neste horário. Não é permitido agendar duas ordens para a mesma equipe no mesmo horário. Escolha outro horário ou outra equipe.
            </p>
            <button
              type="button"
              onClick={() => setShowConflitoModal(false)}
              style={{
                padding: '12px 32px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Entendi
            </button>
          </div>
        </div>,
        document.body
      )}

      {showConflitoInstalacaoModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999999,
            padding: '20px'
          }}
          onClick={() => setShowConflitoInstalacaoModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              padding: '32px 24px',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⚠️</div>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.5rem', color: '#333' }}>Instalação já agendada para esta equipe</h3>
            <p style={{ margin: '0 0 24px', fontSize: '1.1rem', color: '#666', lineHeight: 1.6 }}>
              Só é permitida uma instalação por dia para a mesma equipe. Já existe uma ordem de Instalação ativa (Novo, Em andamento ou Validado) para esta equipe nesta data. Escolha outra data, outra equipe ou aguarde a ordem existente ser finalizada ou cancelada.
            </p>
            <button
              type="button"
              onClick={() => setShowConflitoInstalacaoModal(false)}
              style={{
                padding: '12px 32px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Entendi
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Sucesso */}
      {showSuccessModal && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          width: '100vw',
          height: '100vh',
          padding: '20px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            position: 'relative',
            zIndex: 1000000,
            animation: 'slideUp 0.3s ease-out'
          }}>
            {/* Header do Modal */}
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '24px 30px',
              borderRadius: '16px 16px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>✅ Sucesso</h3>
                <p style={{ margin: '4px 0 0 0', opacity: 0.9, fontSize: '0.9rem' }}>Operação realizada com sucesso</p>
              </div>
              <button 
                onClick={closeSuccessModal} 
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  color: 'white',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                title="Fechar"
              >
                ×
              </button>
            </div>
            
            {/* Conteúdo do Modal */}
            <div style={{ padding: '30px' }}>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ 
                  background: '#ecfdf5', 
                  border: '1px solid #a7f3d0', 
                  borderRadius: '8px', 
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <p style={{ margin: 0, fontSize: '16px', color: '#065f46', lineHeight: '1.5' }}>
                    {successMessage}
                  </p>
                </div>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                  A operação foi concluída com sucesso. Você pode continuar trabalhando normalmente.
                </p>
              </div>

              {/* Botão de Fechar */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center'
              }}>
                <button 
                  onClick={closeSuccessModal}
                  style={{ 
                    padding: '12px 32px', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: 'pointer', 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-1px)'
                    e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)'
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)'
                    e.target.style.boxShadow = 'none'
                  }}
                >
                  Ótimo!
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default OrdemServico